import { useParams, Link } from 'react-router-dom';
import { articles } from '@/mocks/articles';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find(a => a.id === id);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center bg-background-50">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-300"></i>
            </div>
            <h1 className="font-heading font-bold text-xl text-foreground-950 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该文章可能已被删除或链接无效</p>
            <Link to="/" className="inline-flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 cursor-pointer">
              <i className="ri-arrow-left-line w-4 h-4 flex items-center justify-center"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = articles
    .filter(a => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 4);

  // JSON-LD Schema for Article
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    image: article.coverImage,
    publisher: {
      '@type': 'Organization',
      name: '黑料网在线',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://heiliao.online/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://heiliao.online',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: article.category,
        item: `https://heiliao.online/category/${article.categorySlug}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: article.title,
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />

      <Navbar />
      <Breadcrumb />

      <main>
        <article className="py-8 md:py-10 bg-background-50">
          <div className="container-custom">
            <div className="max-w-3xl mx-auto">
              {/* Meta */}
              <div className="flex items-center gap-2 mb-4 flex-wrap">
                <Link
                  to={`/category/${article.categorySlug}`}
                  className="px-3 py-1 text-xs font-medium bg-primary-100 text-primary-700 rounded-full cursor-pointer whitespace-nowrap"
                >
                  {article.category}
                </Link>
                {article.isHot && (
                  <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">热门</span>
                )}
                {article.isNew && (
                  <span className="px-3 py-1 text-xs font-medium bg-accent-100 text-accent-700 rounded-full whitespace-nowrap">最新</span>
                )}
              </div>

              {/* Title */}
              <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>

              {/* Author & Info */}
              <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 pb-6 border-b border-background-200/70 flex-wrap">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line w-4 h-4 flex items-center justify-center"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line w-4 h-4 flex items-center justify-center"></i>
                  {article.publishDate}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-eye-line w-4 h-4 flex items-center justify-center"></i>
                  {article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万 阅读` : `${article.views} 阅读`}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-message-2-line w-4 h-4 flex items-center justify-center"></i>
                  {article.comments} 评论
                </span>
              </div>

              {/* Cover Image */}
              <div className="rounded-lg overflow-hidden mb-8">
                <img
                  src={article.coverImage}
                  alt={article.title}
                  title={`${article.title} - 黑料网在线`}
                  className="w-full h-auto"
                />
              </div>

              {/* Content */}
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed mb-8 [&>p]:mb-4 [&>p]:text-foreground-700 [&>p]:leading-loose"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8 pt-6 border-t border-background-200/70">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 text-xs font-medium rounded-full bg-background-100 text-foreground-600 border border-background-200/50 cursor-pointer hover:bg-primary-50 hover:text-primary-600 transition-colors whitespace-nowrap"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              {/* Share */}
              <div className="flex items-center gap-3 mb-8">
                <span className="text-sm text-foreground-500">分享到：</span>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="微博">
                  <i className="ri-weibo-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="微信">
                  <i className="ri-wechat-line"></i>
                </button>
                <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-50 text-foreground-500 hover:text-primary-500 transition-colors cursor-pointer" aria-label="复制链接">
                  <i className="ri-link"></i>
                </button>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 bg-background-100">
            <div className="container-custom">
              <h2 className="font-heading font-bold text-xl text-foreground-950 mb-6 md:mb-8">
                相关爆料
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {relatedArticles.map((a) => (
                  <Link key={a.id} to={`/article/${a.id}`} className="group block cursor-pointer">
                    <article className="bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-colors">
                      <div className="aspect-[16/10] overflow-hidden">
                        <img
                          src={a.coverImage}
                          alt={a.title}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        />
                      </div>
                      <div className="p-3 md:p-4">
                        <h3 className="font-heading font-semibold text-sm text-foreground-950 line-clamp-2 group-hover:text-primary-500 transition-colors">
                          {a.title}
                        </h3>
                        <p className="text-xs text-foreground-400 mt-2">{a.publishDate}</p>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}